// 版本生命周期共享件（feat/version-lifecycle）。
// DiffView：把后端 RevisionDiff 渲染成「人话」改动说明（给提供方看，不是自动审核工单）。
// PublishDialog：自包含的「发布」弹窗 —— 拉 diffPreview → 展示改动 → 提交审核（submitRevision，走审核门）。
// 「保存」与「发布」都在各编辑段（基本信息 / 管理 API）的右上角；版本切换在左上角下拉。
import React, { useEffect, useState } from 'react';
import { XPrim } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/index.js';

export const CHANGE_TEXT = { added: '新增', modified: '修改', removed: '删除' };
export const CHANGE_COLOR = { added: '#16a34a', modified: '#b45309', removed: '#dc2626' };

// ── 审核 outcome（后端 reviewOutcome 派生）的单一真源：徽标 + 人话解释 + Badge 组件 ──
// 版本面板（version-panel）、审核详情弹窗（review-report）共用，避免三处各写一份漂移。
// 审核不阻塞 provider：除 reviewing（测试进行中）外，非 rejected 的终态对 provider 都是「已上线」。
//   reviewing / passed / passed_pending / rejected /（pending_human 仅遗留数据）
export const REVIEW_OUTCOME = {
  reviewing: { text: '审核中', bg: 'rgba(59,130,246,0.15)', fg: '#2563eb' },
  passed: { text: '✓ 已上线', bg: 'rgba(34,197,94,0.15)', fg: '#16a34a' },
  // 对 provider 就是「已上线」；「已抽查」只是个轻量副标，spot-check 是管理员的事，不阻塞。
  passed_pending: { text: '✓ 已上线', bg: 'rgba(34,197,94,0.15)', fg: '#16a34a' },
  rejected: { text: '✗ 已驳回', bg: 'rgba(239,68,68,0.15)', fg: '#dc2626' },
  pending_human: { text: '待人工', bg: 'var(--bg-1)', fg: 'var(--fg-2)' },
};
// outcome → 「是什么意思 + 接下来怎么办」。banner 配色用 fg。
export const REVIEW_EXPLAIN = {
  reviewing: { fg: '#2563eb', text: 'AI 正在自主测试端点，请稍候 —— 线上不受影响，通过后自动上线。' },
  passed: { fg: '#16a34a', text: '已通过自动化测试并上线。' },
  passed_pending: { fg: '#16a34a', text: '已上线。系统标记了少量复核点，由管理员事后抽查 —— 不影响你的上线，无需等待。' },
  rejected: { fg: '#dc2626', text: '未通过自动化测试，已驳回到沙箱。修复下方问题后可重新提交审核（可多次提交）。线上的旧版本继续在跑。' },
  pending_human: { fg: '#b45309', text: '（遗留状态）等待人工复核。新提交的审核已不会进入此状态。' },
};
export const ReviewBadge = ({ outcome }) => {
  const m = REVIEW_OUTCOME[outcome];
  if (!m) return null;
  return <span style={{ padding: '1px 7px', borderRadius: 9, fontSize: 10.5, fontWeight: 600, background: m.bg, color: m.fg }}>{m.text}</span>;
};
// diff 引擎的内部字段名 → 给提供方看的人话
const FIELD_LABEL = {
  upstream: '路径', baseRef: '上游环境', method: '请求方法',
  requestSchema: '请求体', params: '查询参数', headers: '请求头',
  pathParams: '路径参数', responseSchema: '响应格式',
  billingType: '计费方式', costPerCall: '单次价格', tokenPricing: 'Token 价格',
  costPerResource: '资源价格', dynamicRules: '计费规则',
  name: '名称', description: '描述', group: '分组', hidden: '显示/隐藏',
  authConfig: '认证方式',
  // WebSocket 端点
  protocol: '传输协议', wsConfig: 'WebSocket 配置', wsBilling: 'WebSocket 计费',
};

// 全局改动（上游环境 / 认证）→ 人话短句。这些是版本级改动，不挂在单个端点上。
const globalChangeLines = (g) => {
  if (!g) return [];
  const out = [];
  if (g.baseUrls) {
    const parts = [];
    if (g.baseUrls.added?.length) parts.push(`新增环境 ${g.baseUrls.added.join('、')}`);
    if (g.baseUrls.changed?.length) parts.push(`地址变更 ${g.baseUrls.changed.join('、')}`);
    if (g.baseUrls.removed?.length) parts.push(`移除环境 ${g.baseUrls.removed.join('、')}`);
    if (parts.length) out.push(`上游地址：${parts.join('；')}`);
  }
  if (g.authChanged) out.push('认证方式已变更（影响全部端点）');
  return out;
};

// 端点在 diff 列表里的人话标识。WS 端点没有 method/path，显示成
// "WebSocket · <adapter> · $X/min"，别让它渲染成空 method（见后端 wsConfig/wsBilling）。
const endpointLabel = (a) => {
  const isWs = a && (a.protocol === 'WEBSOCKET' || a.wsConfig || a.wsBilling);
  if (isWs) {
    const adapter = a.wsConfig?.adapter || a.adapter;
    const perMin = a.wsBilling?.perMinuteUsd ?? a.perMinuteUsd;
    return ['WebSocket', adapter, perMin != null ? `$${perMin}/min` : null, a.name]
      .filter(Boolean)
      .join(' · ');
  }
  return `${a.method || ''} ${a.path || ''}`.trim() || a.name || '(端点)';
};

export const DiffView = ({ diff }) => {
  if (!diff) return <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>无工作副本 / 无改动。</div>;
  const { summary, affectedEndpoints, globalChanges } = diff;
  const gLines = globalChangeLines(globalChanges);
  if (!affectedEndpoints.length && !gLines.length)
    return <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>与线上一致，无改动。</div>;

  const c = summary.counts;
  // 首次上线：无任何线上基线（没有 unchanged/modified/removed），所有端点都是「新增」。
  const firstPublish = c.added > 0 && c.modified === 0 && c.removed === 0 && c.unchanged === 0;

  return (
    <div>
      {/* 概要 */}
      <div style={{ fontSize: 13, color: 'var(--fg-1)', marginBottom: 8 }}>
        本次改动：
        <b style={{ color: CHANGE_COLOR.added }}>新增 {c.added}</b> ·{' '}
        <b style={{ color: CHANGE_COLOR.modified }}>修改 {c.modified}</b> ·{' '}
        <b style={{ color: CHANGE_COLOR.removed }}>删除 {c.removed}</b>
      </div>
      {firstPublish && (
        <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 8 }}>
          这是该版本首次上线，下列 {c.added} 个端点将全部发布。
        </div>
      )}

      {/* 版本级（全局）改动 */}
      {gLines.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--fg-2)', background: 'var(--bg-1)', border: '1px solid var(--bd-1)', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
          {gLines.map((l, i) => (<div key={i}>· {l}</div>))}
        </div>
      )}

      {/* 上游重测提示（人话） */}
      {summary.requiresUpstreamTest && (
        <div style={{ fontSize: 12, color: '#b45309', marginBottom: 10 }}>
          ⚠ 上游地址或接口有变化，上线前建议重新验证连通性。
        </div>
      )}

      {/* 受影响端点 */}
      {affectedEndpoints.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {affectedEndpoints.map((a, i) => (
            <div key={i} style={{ border: '1px solid var(--bd-1)', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: CHANGE_COLOR[a.change] }}>{CHANGE_TEXT[a.change] || a.change}</span>
                <span style={{ fontFamily: 'monospace' }}>{endpointLabel(a)}</span>
              </div>
              {a.change === 'modified' && a.changedFields?.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 3 }}>
                  改了：{a.changedFields.map((f) => FIELD_LABEL[f] || f).join('、')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * 发布弹窗：自包含。打开时拉 diffPreview(apiId, major) 展示「改了什么 · 发布到 V{major}」，
 * 「确认上线」调 submitRevision(apiId, workingId)（走审核门）：
 *   - 改动需测 → 提交自动化测试审核（IN_REVIEW），通过后自动上线；
 *   - 改动无需测（仅元数据/计费）→ 后端直接 finalize 上线（autoPromoted）。
 * workingId = 刚保存进去的工作副本版本 id（由调用方在保存后传入）。
 */
// diff → 一份可编辑的 changelog 草稿，让服务商改而不是从零写。
const draftChangelog = (diff) => {
  if (!diff) return '';
  const lines = [...globalChangeLines(diff.globalChanges)];
  for (const a of diff.affectedEndpoints || []) {
    lines.push(`${CHANGE_TEXT[a.change] || a.change} ${a.method} ${a.path}`);
  }
  return lines.join('\n');
};

export const PublishDialog = ({ open, apiId, major, workingId, onClose, onPublished }) => {
  const { toast } = XPrim.useToast();
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [changelog, setChangelog] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setDiff(null);
    setChangelog('');
    XApi.apiSvc.diffPreview(apiId, major)
      .then((d) => { if (!cancelled) { setDiff(d); setChangelog(draftChangelog(d)); } })
      .catch((e) => { if (!cancelled) toast({ title: e?.message || '加载改动失败', type: 'error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // toast 不稳定（每渲染重建），放进依赖会触发重复请求；只依赖稳定值。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiId, major]);

  // 预告会不会触发测试（与后端 gate 同款判断：requiresUpstreamTest；无 diff 保守判要测）。
  const needsReview = diff ? !!diff.summary?.requiresUpstreamTest : true;

  const doSubmit = async () => {
    if (!workingId) return;
    setBusy(true);
    try {
      const res = await XApi.apiSvc.submitRevision(apiId, workingId, { changelog: changelog.trim() });
      if (res?.autoPromoted) {
        toast({ title: `已发布到 v${major}（改动无需测试，直接上线）`, type: 'success' });
      } else {
        toast({ title: `已提交自动审核（审核中）·通过后自动上线`, type: 'info' });
      }
      onPublished?.();
    } catch (e) {
      toast({ title: e?.message || '提交失败', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={560}>
      <DialogHeader title={`发布 · v${major}`} onClose={onClose} />
      <DialogBody>
        {loading ? (
          <div style={{ color: 'var(--fg-3)' }}>计算改动中…</div>
        ) : (
          <>
            <DiffView diff={diff} />
            <div style={{ marginTop: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg-1)', marginBottom: 6 }}>
                本次更新说明（Changelog，可编辑）
              </label>
              <textarea
                className="input"
                rows={4}
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                placeholder="写一句人话说明这次改了什么，会展示在 Changelog 并推送给订阅了这个 API 的用户"
                style={{ width: '100%', fontSize: 12, lineHeight: 1.6, resize: 'vertical' }}
                maxLength={2000}
              />
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.6 }}>
              {needsReview ? (
                <>此改动将<b style={{ color: '#2563eb' }}>触发自动化测试审核</b>（AI 自主测端点），<b style={{ color: 'var(--fg-1)' }}>通过后自动发布到 v{major}</b>。</>
              ) : (
                <>改动轻微（仅元数据/计费），无需测试，确认后<b style={{ color: 'var(--fg-1)' }}>直接发布到 v{major}</b>。</>
              )}
            </div>
          </>
        )}
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn btn-sm" onClick={onClose}>取消</button>
        <button type="button" className="btn btn-sm btn-primary" disabled={busy || loading} onClick={doSubmit}>
          {busy ? '提交中…' : needsReview ? '提交审核并上线' : '确认上线'}
        </button>
      </DialogFooter>
    </Dialog>
  );
};
